import React, { useState, useEffect, useRef } from "react";
import dayjs from "dayjs";
import { useFeatureIsOn } from "@growthbook/growthbook-react";

import {
  StyledEngineProvider,
  Grid,
  Typography,
  Button,
  Tooltip,
  IconButton,
  Box,
  Avatar,
  useTheme,
  ThemeProvider
} from "@mui/material";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { faFileArrowUp } from "@fortawesome/pro-solid-svg-icons";

import { LoadingButton, TextField } from "@aclymatepackages/atoms";
import { YesNoQuestion } from "@aclymatepackages/modules";
import { editObjectData } from "@aclymatepackages/array-immutability-helpers";
import mainTheme, { mergeDarkTheme } from "@aclymatepackages/themes";
import { utilitiesProviders } from "@aclymatepackages/lists";
import { subcategories } from "@aclymatepackages/subcategories";
import { letterSBoolean, formatDate } from "@aclymatepackages/formatters";

import SettingsObjectDetailsSlider from "../../SettingsObjectDetailsSlider";
import EmissionsDetails from "../../EmissionsDetails";

import WarningPopup from "../../../../atoms/notifications/WarningPopup";
import EmissionsDetailsWarning from "../../../../atoms/notifications/EmissionsDetailsWarning";
import DatePicker from "../../../../atoms/mui/DatePicker";
import AnalyticsFeatureIcon from "../../../../atoms/premium-analytics/AnalyticsFeatureIcon";

import PlacesAutocomplete from "../../../../inputs/autocomplete/PlacesAutocomplete";
import FacilityTypeSelect from "../../../../inputs/offices/FacilityTypeSelect";
import ElectricIntensityAndUnitsInput from "../../../../inputs/offices/ElectricIntensityAndUnitsInput";
import ElectricRenewablesPercentageInput from "../../../../inputs/offices/ElectricRenewablesPercentageInput";
import OfficeInput from "../../../../inputs/tags/OfficeInput";
import MultipartForm from "../../../../inputs/flows/MultipartForm";
import LinkUtilitiesForm from "../../../../inputs/offices/LinkUtilitiesForm";
import FuelTypeToggleButtons from "../../../../atoms/inputs/FuelTypeToggleButtons";

import {
  useOfficeStatuses,
  electricRenewablesAndIntensitySaveEnabled,
  electricRecsPercentageError,
  useNewOfficesSubmit,
  useBuildNewOfficeInputFormRows
} from "../../../../../helpers/components/offices";
import {
  buildNewEmployeeStatusLinks,
  sendEmployeeSurvey,
  useTerminateEmployee
} from "../../../../../helpers/components/employees";
import { useSharedFormLoading } from "../../../../../helpers/components/inputs";
import { serviceClassDescription } from "../../../../../helpers/components/utilitiesApi";
import {
  useAccountData,
  useCachedFirebaseCrud,
  useCachedDisplayData
} from "../../../../../helpers/firebase";
import { setAddress } from "../../../../../helpers/utils/geography";
import { fetchOurApi } from "../../../../../helpers/utils/apiCalls";
import usePlatformLayoutContext from "../../../../../helpers/contexts/platformLayout";
import {
  useStarterTierSubscriptionFlags,
  useSubscriptionType
} from "../../../../../helpers/hooks/companyData";
import useAccountingData from "../../../../../helpers/hooks/accountingData";
import usePermissions from "../../../../../helpers/hooks/usePermissions";

const LinkedUtilityDisplayBlock = ({ type, linkedUtility }) => {
  const { palette } = useTheme();

  const { utilityId, pairedMeters = [] } = linkedUtility || {};
  const { icon } = subcategories.find(
    ({ subcategory }) => subcategory === type
  );
  const { name: utilityName } = utilitiesProviders.find(
    ({ utilityId: providerId }) => providerId === utilityId
  );
  const backgroundColor = palette[type].main;

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={mergeDarkTheme}>
        <Box
          style={{
            backgroundColor,
            borderRadius: "15px"
          }}
          p={2}
        >
          <Grid container direction="column" spacing={2}>
            <Grid item container spacing={2} alignItems="center" wrap="nowrap">
              <Grid item>
                <Avatar
                  style={{
                    fontSize: "1rem",
                    height: "25px",
                    width: "25px",
                    backgroundColor: "white",
                    borderStyle: "solid",
                    borderColor: "white"
                  }}
                >
                  {icon}
                </Avatar>
              </Grid>
              <Grid item>
                <Typography variant="body1" color="primary">
                  {utilityName}
                </Typography>
              </Grid>
            </Grid>
            {pairedMeters.map(({ serviceAddress, serviceClass }, idx) => (
              <Grid item>
                <Typography variant="body2" color="primary">{`Meter #${
                  idx + 1
                } (${serviceClassDescription(serviceClass)})`}</Typography>
                <Typography variant="body2" color="primary">
                  {serviceAddress}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Box>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

const EditOfficeDetails = ({
  modifyOffice,
  setModifyOffice,
  companyStartDate
}) => {
  const [{ earliestNewEmissionDate }] = useAccountingData();

  const {
    address: modifyAddress,
    sqFootage: modifySqFootage,
    facilityType: modifyFacilityType,
    startDate: modifyStartDate,
    currentCommuteEmployees = [],
    isCommutable: modifyIsCommutable = true
  } = modifyOffice;

  const editOffice = (field) => (value) =>
    editObjectData(setModifyOffice, field, value);

  return (
    <Grid container spacing={2}>
      <Grid item sm={12}>
        <PlacesAutocomplete
          label="What's the address?"
          place={modifyAddress || null}
          editPlace={setAddress(editOffice("address"))}
          size="small"
        />
      </Grid>
      <Grid item sm={5}>
        <TextField
          label="Sq. Footage"
          value={modifySqFootage}
          setValue={editOffice("sqFootage")}
        />
      </Grid>
      <Grid item sm={7}>
        <FacilityTypeSelect
          facilityType={modifyFacilityType}
          editFacilityType={editOffice("facilityType")}
        />
      </Grid>
      <Grid item sm={12}>
        <DatePicker
          label="Start Date"
          disabled={
            earliestNewEmissionDate &&
            dayjs(modifyStartDate).isBefore(dayjs(earliestNewEmissionDate))
          }
          date={modifyStartDate || companyStartDate}
          minDate={companyStartDate}
          maxDate={new Date()}
          editDate={(date) => editOffice("startDate")(new Date(date))}
        />
      </Grid>
      <Grid item sm={12}></Grid>
      <Grid item>
        <YesNoQuestion
          question="Is this a facility that employees commute to?"
          value={modifyIsCommutable}
          setValue={(value) => editOffice("isCommutable")(value)}
          disabled={currentCommuteEmployees.length > 0}
        />
      </Grid>
    </Grid>
  );
};

const EditOfficePower = ({ modifyOffice, setModifyOffice }) => {
  const {
    electricityInLease,
    electricUtility,
    combinedUtility,
    linkedElectricUtility,
    linkedCombinedUtility,
    electricRenewablesPercentage,
    electricCarbonIntensity,
    electricCarbonIntensityUnits,
    electricRecsPercentage
  } = modifyOffice;

  const editOffice = (field) => (value) =>
    editObjectData(setModifyOffice, field, value);

  const showCombinedProvider =
    electricityInLease === false && (combinedUtility || linkedCombinedUtility);
  const showElectricProvider =
    electricityInLease === false && !showCombinedProvider;

  return (
    <Grid container spacing={2}>
      <Grid item sm={12}>
        <YesNoQuestion
          question="Is electricity included in the lease?"
          value={electricityInLease}
          setValue={editOffice("electricityInLease")}
        />
      </Grid>
      {showCombinedProvider && (
        <Grid item sm={12}>
          {linkedCombinedUtility ? (
            <LinkedUtilityDisplayBlock
              type="utilities"
              linkedUtility={linkedCombinedUtility}
            />
          ) : (
            <TextField
              label="Utility Provider"
              size="small"
              value={combinedUtility}
              setValue={editOffice("combinedUtility")}
            />
          )}
        </Grid>
      )}
      {showElectricProvider && (
        <Grid item sm={12}>
          {linkedElectricUtility ? (
            <LinkedUtilityDisplayBlock
              type="electricity"
              linkedUtility={linkedElectricUtility}
            />
          ) : (
            <TextField
              label="Electric Utility"
              size="small"
              value={electricUtility}
              setValue={editOffice("electricUtility")}
            />
          )}
        </Grid>
      )}
      <Grid item sm={12}>
        <ElectricRenewablesPercentageInput
          electricRenewablesPercentage={electricRenewablesPercentage}
          editElectricRenewablesPercentage={editOffice(
            "electricRenewablesPercentage"
          )}
        />
      </Grid>
      <ElectricIntensityAndUnitsInput
        electricCarbonIntensity={electricCarbonIntensity}
        editElectricCarbonIntensity={editOffice("electricCarbonIntensity")}
        electricCarbonIntensityUnits={electricCarbonIntensityUnits}
        editElectricCarbonIntensityUnits={editOffice(
          "electricCarbonIntensityUnits"
        )}
      />
      <Grid item sm={12}>
        <TextField
          label="Percentage of electricity from RECs"
          value={electricRecsPercentage}
          setValue={editOffice("electricRecsPercentage")}
          error={electricRecsPercentageError(electricRecsPercentage)}
          helperText={
            electricRecsPercentageError(electricRecsPercentage)
              ? "Please enter a percentage between 0 and 100"
              : ""
          }
        />
      </Grid>
    </Grid>
  );
};

const EditOfficeHeat = ({ modifyOffice, setModifyOffice }) => {
  const {
    electricityInLease,
    hasHeat = true,
    heatInLease,
    heatFuelType = "naturalGas",
    gasUtility,
    electricUtility,
    combinedUtility,
    isGasSameAsElectricUtility,
    linkedGasUtility,
    linkedCombinedUtility
  } = modifyOffice;

  const editOffice = (field) => (value) =>
    editObjectData(setModifyOffice, field, value);

  const onHasHeatChange = (value) => {
    editOffice("hasHeat")(value);
    if (value === false) {
      editOffice("heatInLease")(false);
      editOffice("heatFuelType")(undefined);
    }
  };

  const onIsSameUtilityChange = (value) => {
    editOffice("isGasSameAsElectricUtility")(value);
    if (!value) {
      editOffice("combinedUtility")(undefined);
      return;
    }
    const combinedName = gasUtility || electricUtility;
    if (combinedName) {
      editOffice("combinedUtility")(combinedName);
      if (!electricUtility) {
        editOffice("electricUtility")(combinedName);
      }
      if (!gasUtility) {
        editOffice("gasUtility")(combinedName);
      }
    }
  };

  const showCombinedProvider =
    electricityInLease === false &&
    hasHeat !== false &&
    heatInLease === false &&
    (combinedUtility || linkedCombinedUtility);

  const showGasProvider =
    hasHeat !== false && heatInLease === false && !showCombinedProvider;

  const showSameUtilityToggle =
    showGasProvider && electricityInLease === false && gasUtility;

  return (
    <Grid container spacing={2}>
      <Grid item sm={12}>
        <YesNoQuestion
          question="Does this office have heat?"
          value={hasHeat}
          setValue={onHasHeatChange}
        />
      </Grid>
      {hasHeat && (
        <>
          <Grid item sm={12}>
            <YesNoQuestion
              question="Is heat included in the lease?"
              value={heatInLease}
              setValue={editOffice("heatInLease")}
              disabled={hasHeat === false}
            />
          </Grid>
          <Grid item sm={12}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item>
                <Typography variant="body1">Fuel Type: </Typography>
              </Grid>
              <Grid item>
                <FuelTypeToggleButtons
                  value={heatFuelType}
                  onChange={editOffice("heatFuelType")}
                  disabled={hasHeat === false}
                />
              </Grid>
            </Grid>
          </Grid>
        </>
      )}
      {showCombinedProvider && (
        <Grid item sm={12}>
          {linkedCombinedUtility ? (
            <LinkedUtilityDisplayBlock
              type="utilities"
              linkedUtility={linkedCombinedUtility}
            />
          ) : (
            <TextField
              label="Utility Provider"
              size="small"
              value={combinedUtility}
              setValue={editOffice("combinedUtility")}
            />
          )}
        </Grid>
      )}
      {showGasProvider && (
        <Grid item sm={12}>
          {linkedGasUtility ? (
            <LinkedUtilityDisplayBlock
              type="gas"
              linkedUtility={linkedGasUtility}
            />
          ) : (
            <TextField
              label="Gas Utility"
              size="small"
              value={gasUtility}
              setValue={editOffice("gasUtility")}
              disabled={hasHeat === false}
            />
          )}
        </Grid>
      )}
      {showSameUtilityToggle && (
        <Grid item sm={12}>
          <YesNoQuestion
            question="Is this the same utility you get electricity from?"
            value={isGasSameAsElectricUtility}
            setValue={onIsSameUtilityChange}
          />
        </Grid>
      )}
    </Grid>
  );
};

const FooterWithWarnings = ({
  showCloseWarning,
  setShowCloseWarning,
  warningType,
  saveEnabled,
  editsDate,
  setEditsDate,
  onSave,
  saveLoading,
  resetOfficeBackToOriginal,
  setOfficeMovingInterfaceOpen,
  mostRecentChangeDate,
  officeHasNoEmployees,
  closeOfficeWithoutEmployees
}) => {
  const { canEdit } = usePermissions();
  const [{ startDate: companyStartDate }] = useAccountData();
  const [{ earliestNewEmissionDate }] = useAccountingData();

  const buildWarningProps = () => {
    if (showCloseWarning) {
      return {
        title: "When did you close this office?",
        input: (
          <Grid
            container
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
          >
            <Grid item sm={7}>
              <DatePicker
                darkTheme
                label="Please select a date"
                size="small"
                date={editsDate}
                editDate={setEditsDate}
                minDate={
                  earliestNewEmissionDate ||
                  mostRecentChangeDate ||
                  companyStartDate
                }
                maxDate={new Date()}
              />
            </Grid>
            <Grid item>
              <StyledEngineProvider injectFirst>
                <ThemeProvider theme={mergeDarkTheme}>
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={() => closeOfficeWithoutEmployees(editsDate)}
                    disabled={!canEdit}
                  >
                    Close Office
                  </Button>
                </ThemeProvider>
              </StyledEngineProvider>
            </Grid>
          </Grid>
        )
      };
    }

    const warningTypes = {
      date: {
        title:
          "You need to specify the date that these changes occurred for this office. ",
        input: (
          <DatePicker
            darkTheme
            label="When did you make these changes to this office?"
            size="small"
            date={editsDate}
            editDate={setEditsDate}
            minDate={
              earliestNewEmissionDate ||
              mostRecentChangeDate ||
              companyStartDate
            }
            maxDate={new Date()}
          />
        ),
        secondaryAction: (
          <ThemeProvider theme={mergeDarkTheme}>
            <Grid container justifyContent="center">
              <Grid item>
                <Button onClick={() => onSave()} color="primary">
                  This office wasn't changed
                </Button>
              </Grid>
            </Grid>
          </ThemeProvider>
        )
      },
      moving: {
        title: "You're changing this office's address?",
        input: (
          <Grid
            container
            alignItems="center"
            justifyContent="center"
            spacing={2}
          >
            <Grid
              item
              container
              alignItems="center"
              justifyContent="center"
              xs={12}
            >
              <Typography variant="body2" color="textPrimary">
                Instead of just moving this office, would you instead like to
                close this office and open a new one?
              </Typography>
            </Grid>
            <Grid item>
              <StyledEngineProvider injectFirst>
                <ThemeProvider theme={mergeDarkTheme}>
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={() => setOfficeMovingInterfaceOpen(true)}
                  >
                    Yes
                  </Button>
                </ThemeProvider>
              </StyledEngineProvider>
            </Grid>
            <Grid item>
              <StyledEngineProvider injectFirst>
                <ThemeProvider theme={mergeDarkTheme}>
                  <Button onClick={() => resetOfficeBackToOriginal()}>
                    No
                  </Button>
                </ThemeProvider>
              </StyledEngineProvider>
            </Grid>
          </Grid>
        )
      }
    };

    return warningTypes[warningType] || {};
  };

  const {
    title: warningTitle,
    input: warningInput,
    secondaryAction
  } = buildWarningProps();

  const onDeleteClick = () => {
    if (officeHasNoEmployees) {
      return setShowCloseWarning(true);
    }

    return setOfficeMovingInterfaceOpen(true);
  };

  return (
    <div style={{ position: "relative" }}>
      {warningTitle && (
        <WarningPopup
          warningTitle={warningTitle}
          warningInput={warningInput}
          secondaryAction={secondaryAction}
        />
      )}
      <Grid
        container
        spacing={2}
        justifyContent="space-between"
        alignItems="center"
      >
        <Grid item>
          <Tooltip title="Close this office">
            <span>
              <IconButton
                onClick={onDeleteClick}
                size="large"
                disabled={!canEdit}
              >
                <DeleteForeverIcon color="error" />
              </IconButton>
            </span>
          </Tooltip>
        </Grid>
        <Grid item>
          <LoadingButton
            isLoading={saveLoading}
            label="Save Changes"
            color="secondary"
            disabled={!saveEnabled || !canEdit}
            onClick={onSave}
          />
        </Grid>
      </Grid>
    </div>
  );
};

const MovingEmployeesToAnotherOffice = ({
  oldOffice,
  selectedOffice,
  setSelectedOffice,
  moveStartDate,
  setMoveStartDate,
  isSelectedOffice,
  setIsSelectedOffice
}) => {
  const [companyOffices] = useCachedDisplayData("offices");

  const [{ earliestNewEmissionDate }] = useAccountingData();
  const [{ startDate: companyStartDate }] = useAccountData();
  const { id: oldOfficeId, startDate: oldOfficeStartDate } = oldOffice;
  const [popperAnchorEl, setPopperAnchorEl] = useState(null);
  const [activePopper, setActivePopper] = useState("");

  const availableOffices = companyOffices.filter(
    ({ id: officeId, endDate }) => officeId !== oldOfficeId && !endDate
  );

  const propsObj = {
    popperAnchorEl,
    setPopperAnchorEl,
    activePopper,
    setActivePopper,
    disableInputCardResizing: true
  };

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={mainTheme}>
        <Grid container justifyContent="center" spacing={2}>
          <Grid item>
            <YesNoQuestion
              question={`Would you like all current employees in ${oldOffice.name} to move to another office?`}
              value={isSelectedOffice}
              setValue={setIsSelectedOffice}
            />
          </Grid>
          {isSelectedOffice && (
            <>
              <Grid item>
                <Typography variant="h6" align="center">
                  {`Select an office that you would like all current employees in ${oldOffice.name} to move to.`}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <OfficeInput
                  office={selectedOffice}
                  setOffice={setSelectedOffice}
                  title="Select an Office"
                  transactionSubcategory="anyUtilities"
                  position="center"
                  availableOffices={availableOffices}
                  {...propsObj}
                />
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <Typography variant="h6" align="center">
              When did this office move occur?
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <DatePicker
              label="Move date"
              darkTheme={true}
              date={moveStartDate}
              minDate={
                earliestNewEmissionDate ||
                oldOfficeStartDate ||
                companyStartDate
              }
              maxDate={new Date()}
              editDate={(date) => setMoveStartDate(new Date(date))}
            />
          </Grid>
        </Grid>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

const MovingEmployeesToNewOfficeQuestion = ({
  oldOffice,
  moveEmployeesToNewOffice,
  sendSurveyToEmployees
}) => (
  <StyledEngineProvider injectFirst>
    <ThemeProvider theme={mainTheme}>
      <Grid container justifyContent="center" spacing={2}>
        <Grid item>
          <Typography variant="h6" align="center">
            {`Would you like all of the current employees in ${oldOffice.name} to now commute to the new office you just created?`}
          </Typography>
        </Grid>
        <Grid container spacing={2} justifyContent="center">
          <Grid item>
            <Button
              color="primary"
              onClick={() => moveEmployeesToNewOffice()}
              variant="contained"
              endIcon={<ChevronRightIcon fontSize="large" />}
            >
              Yes
            </Button>
          </Grid>
          <Grid item>
            <Button
              color="secondary"
              onClick={() => sendSurveyToEmployees()}
              variant="contained"
              endIcon={<ChevronRightIcon fontSize="large" />}
            >
              No
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </ThemeProvider>
  </StyledEngineProvider>
);

const useCloseOffice = () => {
  const { updateCollectionDoc } = useCachedFirebaseCrud();

  return async (closedOffice, closedOfficeEndDate) => {
    const { id: closedOfficeId, changes: closedOfficeChanges = [] } =
      closedOffice;

    const closeOfficeChange = {
      date: new Date(),
      status: "closed",
      endDate: closedOfficeEndDate
    };
    const oldOfficeNewChanges = [closeOfficeChange, ...closedOfficeChanges];

    await updateCollectionDoc("offices", closedOfficeId, {
      changes: oldOfficeNewChanges,
      endDate: closedOfficeEndDate
    });
  };
};

const useCloseOfficeAndTerminateEmployees = (office, setOpen) => {
  const { palette } = useTheme();
  const { startDate: officeStartDate, currentCommuteEmployees } = office;

  const [dbEmployees] = useCachedDisplayData("employees");
  const terminateEmployee = useTerminateEmployee();
  const [{ startDate: companyStartDate }] = useAccountData();

  const [terminationDate, setTerminationDate] = useState(null);

  const closeOffice = useCloseOffice();

  const onCloseOfficeAndTerminateEmployees = () => {
    const thisOfficeDbEmployees = dbEmployees.filter(({ id: employeeId }) =>
      currentCommuteEmployees.find(
        (commuteEmployee) => commuteEmployee.id === employeeId
      )
    );

    closeOffice(office, terminationDate);
    Promise.all(
      thisOfficeDbEmployees.map(async (employee) =>
        terminateEmployee(employee, terminationDate)
      )
    );
    return setOpen(false);
  };

  return [
    {
      label: "Close Office",
      input: (
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <DatePicker
              label="What date did you close this office?"
              date={terminationDate}
              editDate={setTerminationDate}
              minDate={officeStartDate || companyStartDate}
              maxDate={new Date()}
            />
          </Grid>
          {terminationDate && (
            <Grid item>
              <WarningPopup
                backgroundColor={palette.warning.main}
                warningTitle={`By doing this you are also terminating the ${
                  currentCommuteEmployees.length
                } employee${letterSBoolean(
                  currentCommuteEmployees
                )} who currently work${letterSBoolean(
                  currentCommuteEmployees,
                  true
                )} here as of ${formatDate(terminationDate)}.`}
              />
            </Grid>
          )}
        </Grid>
      ),
      buttonDisabled: !terminationDate,
      buttonText: "Close Office and Terminate Employees",
      onAdvanceForm: onCloseOfficeAndTerminateEmployees
    }
  ];
};

const useMoveEmployeesToOffice = () => {
  const [employees] = useCachedDisplayData("employees");

  const { updateCollectionDoc } = useCachedFirebaseCrud();

  const closeOffice = useCloseOffice();

  const findEmployeesThatCommuteToOffice = ({ id: officeId }) => {
    return employees.filter(({ commuteSchedules }) => {
      if (!Array.isArray(commuteSchedules)) {
        return false;
      }
      const allCommuteEndpoints = commuteSchedules.flatMap(
        ({ commuteEndpoints }) => commuteEndpoints
      );
      return allCommuteEndpoints.find(({ id }) => id === officeId);
    });
  };

  const modifyCommuteEndpointToNewOffice = async (
    commuteSchedule,
    newOffice,
    newOfficeId,
    oldOffice
  ) => {
    const { commuteEndpoints, home } = commuteSchedule;
    const { address: employeeHomeAddress } = home || {};
    const { id: oldOfficeId } = oldOffice;
    const { address: newCommuteAddress, name: newOfficeName } = newOffice;

    return await Promise.all(
      commuteEndpoints.map(async (commuteEndpoint) => {
        const { id: currentOfficeId } = commuteEndpoint;

        if (currentOfficeId === oldOfficeId) {
          const { totalMileage: newOneWayDistanceMi } = await fetchOurApi({
            path: "/calcs/cars/directions-mileage",
            method: "POST",
            data: {
              to: newCommuteAddress,
              from: employeeHomeAddress
            },
            callback: (res) => res
          });

          return {
            ...commuteEndpoint,
            id: newOfficeId,
            name: newOfficeName,
            address: newCommuteAddress,
            oneWayDistanceMi: newOneWayDistanceMi
          };
        }

        return commuteEndpoint;
      })
    );
  };

  const changeEmployeeCommuteEndpointToNewOffice = async (
    { commuteSchedules },
    newOffice,
    newOfficeId,
    oldOffice,
    moveStartDate
  ) =>
    await Promise.all(
      commuteSchedules.map(async (commuteSchedule, idx) =>
        idx === 0
          ? {
              ...commuteSchedule,
              startDate: dayjs(moveStartDate).toDate(),
              commuteEndpoints: await modifyCommuteEndpointToNewOffice(
                commuteSchedule,
                newOffice,
                newOfficeId,
                oldOffice
              )
            }
          : commuteSchedule
      )
    );

  const changeEmployeesCommuteSchedulesToNewOffice = async (
    employees,
    newOffice,
    newOfficeId,
    oldOffice,
    moveStartDate
  ) => {
    const employeesThatCommuteToNewOffice = await Promise.all(
      employees.map(async (employee) => ({
        ...employee,
        commuteSchedules: await changeEmployeeCommuteEndpointToNewOffice(
          employee,
          newOffice,
          newOfficeId,
          oldOffice,
          moveStartDate
        )
      }))
    );

    return employeesThatCommuteToNewOffice.map(
      ({ id: employeeId, commuteSchedules: newCommuteSchedulesToNewOffice }) =>
        updateCollectionDoc("employees", employeeId, {
          commuteSchedules: newCommuteSchedulesToNewOffice
        })
    );
  };

  const endEmployeesCurrentCommuteSchedules = (employees, oldOfficeEndDate) =>
    employees.map((employee) => {
      const { commuteSchedules: currentCommuteSchedules } = employee;
      const [currentCommuteScheduleCopy, ...previousCommuteSchedules] =
        currentCommuteSchedules;
      const endedCurrentCommuteSchedule = {
        ...currentCommuteScheduleCopy,
        endDate: oldOfficeEndDate
      };
      const newCommuteSchedules = [
        currentCommuteScheduleCopy,
        endedCurrentCommuteSchedule,
        ...previousCommuteSchedules
      ];

      return {
        ...employee,
        commuteSchedules: newCommuteSchedules
      };
    });

  const sendOldOfficeCommutersSurveys = async (employees) =>
    await Promise.all(
      employees.map(async (employee) => {
        const { id: employeeId } = employee;
        await sendEmployeeSurvey({ employee });

        return await updateCollectionDoc("employees", employeeId, {
          links: buildNewEmployeeStatusLinks(employee, "incomplete")
        });
      })
    );

  const changeEmployeesToCommuteToNewOffice = async ({
    newOffice,
    newOfficeId,
    oldOffice,
    givenMoveStartDate
  }) => {
    const { startDate: newOfficeStartDate } = newOffice;
    const moveStartDate = givenMoveStartDate || newOfficeStartDate;
    const oldOfficeEndDate = dayjs(moveStartDate).subtract(1, "day").toDate();
    const commuteEmployeesToOldOffice =
      findEmployeesThatCommuteToOffice(oldOffice);

    const employeesWithEndedCurrentCommuteSchedules =
      endEmployeesCurrentCommuteSchedules(
        commuteEmployeesToOldOffice,
        oldOfficeEndDate
      );

    await changeEmployeesCommuteSchedulesToNewOffice(
      employeesWithEndedCurrentCommuteSchedules,
      newOffice,
      newOfficeId,
      oldOffice,
      moveStartDate
    );
    return await closeOffice(oldOffice, oldOfficeEndDate);
  };

  const sendSurveysToOldOfficeCommuters = async ({
    oldOffice,
    givenMoveStartDate
  }) => {
    const oldOfficeEndDate = dayjs(givenMoveStartDate)
      .subtract(1, "day")
      .toDate();
    const commuteEmployeesToOldOffice =
      findEmployeesThatCommuteToOffice(oldOffice);
    await sendOldOfficeCommutersSurveys(commuteEmployeesToOldOffice);
    return await closeOffice(oldOffice, oldOfficeEndDate);
  };

  return {
    changeEmployeesToCommuteToNewOffice,
    sendSurveysToOldOfficeCommuters
  };
};

const useMoveToNewOffice = ({ oldOffice, setOpen }) => {
  const isMountedRef = useRef(true);
  const [offices, officesLoading] = useCachedDisplayData("offices");
  const buildNewOfficeInputFormRows = useBuildNewOfficeInputFormRows();
  const onNewOfficesSubmit = useNewOfficesSubmit();
  const { setFormLoading } = useSharedFormLoading();
  const { activateSnackbar } = usePlatformLayoutContext();
  const {
    changeEmployeesToCommuteToNewOffice,
    sendSurveysToOldOfficeCommuters
  } = useMoveEmployeesToOffice();
  const [{ startDate: companyStartDate, name: companyName }] = useAccountData();
  const [{ isPaid }] = useSubscriptionType("saas");
  const { isStarterTier } = useStarterTierSubscriptionFlags();
  const utilityApiFeatures = useFeatureIsOn("utilityapi-features");

  const [newOffice, setNewOffice] = useState({});
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [moveStartDate, setMoveStartDate] = useState(null);
  const [isSelectedOffice, setIsSelectedOffice] = useState(null);

  const editOffice = (field, value) =>
    editObjectData(setNewOffice, field, value);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!officesLoading && isMountedRef.current) {
      setNewOffice({ name: `Office #${offices.length}` });
    }
  }, [officesLoading, offices.length]);

  const generateOfficeInfo = async (selectedOffice) => {
    if (selectedOffice) {
      const { id: selectedOfficeId } = selectedOffice;

      return {
        newOfficeId: selectedOfficeId,
        newOffice: selectedOffice
      };
    }

    const { newOfficeIds } = await onNewOfficesSubmit([newOffice]);
    const [newOfficeId] = newOfficeIds;

    return {
      newOfficeId,
      newOffice
    };
  };

  const moveEmployeesToNewOffice = async (selectedOffice) => {
    setFormLoading(true);
    const { newOfficeId, newOffice } = await generateOfficeInfo(selectedOffice);

    await changeEmployeesToCommuteToNewOffice({
      newOffice,
      newOfficeId,
      oldOffice,
      givenMoveStartDate: moveStartDate
    });

    const snackbarMessage = `All of the employees in ${oldOffice.name} now commute to ${newOffice.name} and ${oldOffice.name} was closed.`;
    activateSnackbar({ message: snackbarMessage, alert: "success" });
    setFormLoading(false);
    setOpen(false);
  };

  const sendSurveyToEmployees = async (isCreateNewOffice) => {
    setFormLoading(true);
    isCreateNewOffice && (await onNewOfficesSubmit([newOffice]));
    const { startDate: newOfficeStartDate } = newOffice;

    await sendSurveysToOldOfficeCommuters({
      oldOffice,
      givenMoveStartDate: moveStartDate || newOfficeStartDate
    });

    const snackbarMessage = isCreateNewOffice
      ? `All of the employees in ${oldOffice.name} were sent surveys to update their commute schedules, ${newOffice.name} was created, and ${oldOffice.name} was closed.`
      : `All of the employees in ${oldOffice.name} were sent surveys to update their commute schedules and ${oldOffice.name} was closed.`;
    activateSnackbar({ message: snackbarMessage, alert: "success" });
    setFormLoading(false);
    setOpen(false);
  };

  const FormBottomInput = () => (
    <MovingEmployeesToNewOfficeQuestion
      oldOffice={oldOffice}
      moveEmployeesToNewOffice={() => moveEmployeesToNewOffice()}
      sendSurveyToEmployees={() => sendSurveyToEmployees(true)}
    />
  );

  const moveToNewOfficeForm = [
    {
      label: newOffice.name,
      title: newOffice.name,
      rows: buildNewOfficeInputFormRows({
        office: newOffice,
        editOffice,
        index: 0,
        companyStartDate,
        companyName,
        isUtilityApiAccessible: isPaid && !isStarterTier && utilityApiFeatures
      }),
      FormBottomInput
    }
  ];

  const moveToAnotherOfficeForm = [
    {
      label: "Move to another office",
      input: (
        <MovingEmployeesToAnotherOffice
          oldOffice={oldOffice}
          selectedOffice={selectedOffice}
          setSelectedOffice={setSelectedOffice}
          moveStartDate={moveStartDate}
          setMoveStartDate={setMoveStartDate}
          isSelectedOffice={isSelectedOffice}
          setIsSelectedOffice={setIsSelectedOffice}
        />
      ),
      buttonDisabled: isSelectedOffice === undefined || !moveStartDate,
      buttonText: "Submit",
      onAdvanceForm: () =>
        selectedOffice
          ? moveEmployeesToNewOffice(selectedOffice)
          : sendSurveyToEmployees()
    }
  ];

  return { moveToNewOfficeForm, moveToAnotherOfficeForm };
};

const MovingOfficeInterface = ({ setOpen, oldOffice }) => {
  const [selectedOption, setSelectedOption] = useState("");
  const { moveToNewOfficeForm, moveToAnotherOfficeForm } = useMoveToNewOffice({
    oldOffice,
    setOpen
  });
  const closeAndTerminateEmployeesForm = useCloseOfficeAndTerminateEmployees(
    oldOffice,
    setOpen
  );

  const forms = {
    newOffice: moveToNewOfficeForm,
    moveOffice: moveToAnotherOfficeForm,
    closeOffice: closeAndTerminateEmployeesForm
  };

  return (
    <MultipartForm
      type="officeMoving"
      forms={forms[selectedOption]}
      onClose={() => setOpen(false)}
      setOption={setSelectedOption}
      selectedOption={selectedOption}
      submitLoadingText="Please wait while we complete your office moving process..."
    />
  );
};

const useEditOfficeDetails = (office, setSelectedOffice) => {
  const {
    currentUtilities,
    changes = [],
    id,
    currentCommuteEmployees = []
  } = office;

  const originalOffice = {
    ...office,
    ...currentUtilities,
    isGasSameAsElectricUtility: !!currentUtilities?.combinedUtility,
    isElectricRenewablesPercentageGiven:
      office.electricRenewablesPercentage !== undefined &&
      office.electricRenewablesPercentage !== "",
    isElectricCarbonIntensityGiven:
      office.electricCarbonIntensity !== undefined &&
      office.electricCarbonIntensity !== ""
  };

  const { updateCollectionDoc } = useCachedFirebaseCrud();
  const [{ startDate: companyStartDate }] = useAccountData();

  const closeOffice = useCloseOffice();
  const [showCloseWarning, setShowCloseWarning] = useState(false);
  const [modifyOffice, setModifyOffice] = useState(originalOffice);
  const [editsDate, setEditsDate] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [officeMovingInterfaceOpen, setOfficeMovingInterfaceOpen] =
    useState(false);

  const {
    combinedUtility: modifyCombinedUtility,
    gasUtility: modifyGasUtility,
    electricUtility: modifyElectricUtility,
    facilityType: modifyFacilityType,
    electricRenewablesPercentage: modifyElectricRenewablesPercentage,
    electricCarbonIntensity: modifyElectricCarbonIntensity,
    electricCarbonIntensityUnits: modifyElectricCarbonIntensityUnits,
    electricRecsPercentage: modifyElectricRecsPercentage,
    sqFootage: modifySqFootage,
    electricityInLease: modifyElectricityInLease,
    hasHeat: modifyHasHeat,
    heatInLease: modifyHeatInLease,
    heatFuelType: modifyHeatFuelType,
    isElectricRenewablesPercentageGiven:
      modifyIsElectricRenewablesPercentageGiven,
    isElectricCarbonIntensityGiven: modifyIsElectricCarbonIntensityGiven,
    eGrid: modifyEGrid,
    linkedElectricUtility,
    linkedGasUtility,
    linkedCombinedUtility
  } = modifyOffice;

  const findChangedFields = () => {
    const objectFieldsArray = Object.entries(modifyOffice).map(
      ([field, value]) => {
        const originalValue = originalOffice[field];

        if (!value && value !== false) {
          return null;
        }

        if (field === "facilityType") {
          return originalValue.value !== value.value && field;
        }

        if (originalValue !== value) {
          return field;
        }

        return null;
      }
    );

    return objectFieldsArray.filter((value) => value);
  };

  const changedFields = findChangedFields();

  const buildWarningType = () => {
    const isRenewablesOrIntensityChanged =
      changedFields.includes("electricRenewablesPercentage") ||
      changedFields.includes("electricCarbonIntensity") ||
      changedFields.includes("electricCarbonIntensityUnits") ||
      changedFields.includes("electricRecsPercentage");

    const isUtilitiesLeaseChange =
      changedFields.includes("electricityInLease") ||
      changedFields.includes("heatInLease") ||
      changedFields.includes("hasHeat") ||
      changedFields.includes("heatFuelType");

    if (changedFields.includes("address")) {
      return "moving";
    }

    if (
      isUtilitiesLeaseChange ||
      (changedFields.includes("sqFootage") &&
        (modifyElectricityInLease || modifyHeatInLease)) ||
      isRenewablesOrIntensityChanged
    ) {
      return "date";
    }

    return null;
  };

  const warningType = buildWarningType();

  const enableSaveButtonOnUtilitiesChange = () => {
    const hasLinkedElectric = !!linkedElectricUtility;
    const hasLinkedGas = !!linkedGasUtility;
    const hasLinkedCombined = !!linkedCombinedUtility;

    const powerProviderValid =
      modifyElectricityInLease === true ||
      hasLinkedCombined ||
      hasLinkedElectric ||
      !!modifyElectricUtility ||
      !!modifyCombinedUtility;

    const heatProviderValid =
      modifyHasHeat === false ||
      modifyHeatInLease === true ||
      hasLinkedCombined ||
      hasLinkedGas ||
      !!modifyGasUtility ||
      !!modifyCombinedUtility;

    const heatFuelTypeValid = modifyHasHeat === false || !!modifyHeatFuelType;

    if (changedFields.length) {
      return powerProviderValid && heatProviderValid && heatFuelTypeValid;
    }

    return false;
  };

  const isElectricRenewablesAndIntensitySaveEnabled =
    electricRenewablesAndIntensitySaveEnabled({
      isElectricRenewablesPercentageGiven:
        modifyIsElectricRenewablesPercentageGiven,
      isElectricCarbonIntensityGiven: modifyIsElectricCarbonIntensityGiven,
      electricRenewablesPercentage: modifyElectricRenewablesPercentage,
      electricCarbonIntensity: modifyElectricCarbonIntensity,
      electricCarbonIntensityUnits: modifyElectricCarbonIntensityUnits,
      electricRecsPercentage: modifyElectricRecsPercentage
    });

  const isSaveEnabled = () => {
    if (!warningType && isElectricRenewablesAndIntensitySaveEnabled) {
      return enableSaveButtonOnUtilitiesChange() || changedFields.length;
    }

    if (warningType === "moving") {
      return false;
    }

    if (!isElectricRenewablesAndIntensitySaveEnabled) {
      return false;
    }

    return !!editsDate && enableSaveButtonOnUtilitiesChange();
  };

  const onDetailsChangeSave = async () => {
    const resetForm = (updateObj) => {
      const mergedOffice = { ...office, ...updateObj };
      const { utilities: currentUtilities } = mergedOffice;
      const isGasSameAsElectricUtility = !!currentUtilities?.combinedUtility;
      const isElectricRenewablesPercentageGiven =
        mergedOffice.electricRenewablesPercentage !== undefined &&
        mergedOffice.electricRenewablesPercentage !== "";
      const isElectricCarbonIntensityGiven =
        mergedOffice.electricCarbonIntensity !== undefined &&
        mergedOffice.electricCarbonIntensity !== "";
      setModifyOffice({
        ...mergedOffice,
        ...currentUtilities,
        isGasSameAsElectricUtility,
        isElectricRenewablesPercentageGiven,
        isElectricCarbonIntensityGiven
      });
      setSelectedOffice({ ...mergedOffice, ...currentUtilities });
      return setSaveLoading(false);
    };

    setSaveLoading(true);
    const utilitiesFields = [
      "combinedUtility",
      "gasUtility",
      "electricUtility",
      "electricityInLease",
      "hasHeat",
      "heatInLease",
      "heatFuelType",
      "isGasSameAsElectricUtility",
      "isElectricRenewablesPercentageGiven",
      "isElectricCarbonIntensityGiven",
      "doesOfficeHaveGasUtility",
      "isGasInIncludedUtilities",
      "utilitiesInLease"
    ];
    const updatableFields = changedFields.filter(
      (field) => !utilitiesFields.includes(field)
    );

    const fieldsUpdateObj = Object.fromEntries(
      updatableFields.map((field) => {
        const value = modifyOffice[field];
        return [field, value];
      })
    );

    const buildUtilitiesProps = () => {
      const hasHeatValue = modifyHasHeat !== false;
      const electricityInLeaseValue = modifyElectricityInLease === true;
      const heatInLeaseValue = hasHeatValue && modifyHeatInLease === true;
      const utilitiesInLease =
        electricityInLeaseValue && (!hasHeatValue || heatInLeaseValue);

      const shouldIncludeElectricUtility = electricityInLeaseValue === false;
      const shouldIncludeGasUtility =
        hasHeatValue && heatInLeaseValue === false;

      const baseReturnObj = {
        ...(modifyEGrid ? { eGrid: modifyEGrid } : {}),
        electricityInLease: electricityInLeaseValue,
        hasHeat: hasHeatValue,
        heatInLease: heatInLeaseValue,
        heatFuelType: modifyHeatFuelType,
        utilitiesInLease,
        doesOfficeHaveGasUtility: hasHeatValue,
        isGasInIncludedUtilities: heatInLeaseValue
      };

      if (
        modifyCombinedUtility &&
        shouldIncludeElectricUtility &&
        shouldIncludeGasUtility
      ) {
        return {
          ...baseReturnObj,
          combinedUtility: modifyCombinedUtility
        };
      }

      return {
        ...baseReturnObj,
        ...(shouldIncludeElectricUtility && modifyElectricUtility
          ? { electricUtility: modifyElectricUtility }
          : {}),
        ...(shouldIncludeGasUtility && modifyGasUtility
          ? { gasUtility: modifyGasUtility }
          : {})
      };
    };

    const newUtilities = buildUtilitiesProps();

    if (warningType === "date" && editsDate) {
      const changesObj = {
        utilities: newUtilities,
        ...fieldsUpdateObj,
        date: editsDate.toDate()
      };

      const officeUpdateObj = {
        changes: [changesObj, ...changes]
      };

      await updateCollectionDoc("offices", id, officeUpdateObj);
      return resetForm(changesObj);
    }

    const updateObj = {
      utilities: newUtilities,
      ...fieldsUpdateObj,
      facilityType: modifyFacilityType,
      electricRenewablesPercentage: modifyElectricRenewablesPercentage,
      electricCarbonIntensity: modifyElectricCarbonIntensity,
      electricCarbonIntensityUnits: modifyElectricCarbonIntensityUnits,
      electricRecsPercentage: modifyElectricRecsPercentage,
      sqFootage: Number(modifySqFootage)
    };

    await updateCollectionDoc("offices", id, updateObj);
    return resetForm(updateObj);
  };

  const resetOfficeBackToOriginal = () => setModifyOffice(originalOffice);

  const [firstChange] = changes;

  const closeOfficeWithoutEmployees = (endDate) => {
    closeOffice(office, endDate);
    return setSelectedOffice(null);
  };

  const renderWithMovingInterface = (content) => (
    <>
      {!!officeMovingInterfaceOpen && (
        <MovingOfficeInterface
          setOpen={(value) => {
            setOfficeMovingInterfaceOpen(value);
            return setSelectedOffice(value);
          }}
          oldOffice={office}
        />
      )}
      {content}
    </>
  );

  return {
    detailsContent: renderWithMovingInterface(
      <EditOfficeDetails
        modifyOffice={modifyOffice}
        setModifyOffice={setModifyOffice}
        companyStartDate={companyStartDate}
      />
    ),
    powerContent: renderWithMovingInterface(
      <EditOfficePower
        modifyOffice={modifyOffice}
        setModifyOffice={setModifyOffice}
      />
    ),
    heatContent: renderWithMovingInterface(
      <EditOfficeHeat
        modifyOffice={modifyOffice}
        setModifyOffice={setModifyOffice}
      />
    ),
    footer: (
      <FooterWithWarnings
        showCloseWarning={showCloseWarning}
        setShowCloseWarning={setShowCloseWarning}
        warningType={warningType}
        saveEnabled={isSaveEnabled()}
        editsDate={editsDate}
        setEditsDate={setEditsDate}
        onSave={onDetailsChangeSave}
        saveLoading={saveLoading}
        mostRecentChangeDate={firstChange?.date}
        resetOfficeBackToOriginal={resetOfficeBackToOriginal}
        setOfficeMovingInterfaceOpen={setOfficeMovingInterfaceOpen}
        officeHasNoEmployees={!currentCommuteEmployees.length}
        closeOfficeWithoutEmployees={closeOfficeWithoutEmployees}
      />
    )
  };
};

const OfficeDetailsSlider = ({
  office,
  setSelectedOffice,
  setSelectedTransaction,
  setSelectedCsvUploadOffice,
  setSelectedOfficeCsvUpload
}) => {
  const {
    id,
    endDate,
    status,
    currentUtilities,
    name,
    address,
    linkedCombinedUtilityId,
    linkedElectricUtilityId,
    linkedGasUtilityId,
    emissions
  } = office;

  const [utilitiesFormOpen, setUtilitiesFormOpen] = useState(false);

  const { electricityInLease, heatInLease, hasHeat, combinedUtility } =
    currentUtilities || {};
  const { state: officeState } = address || {};

  const availableUtilitiesProviders = utilitiesProviders.filter(
    ({ states, utilityId }) =>
      states.includes(officeState) ||
      (utilityId === "DEMO" &&
        process.env.REACT_APP_ENVIRONMENT !== "production")
  );
  const availableUtilitiesProvidersString = availableUtilitiesProviders
    .map(({ name }, idx) =>
      idx !== availableUtilitiesProviders?.length - 1 ? `${name}, ` : `${name}`
    )
    .join("");
  const doesOfficeHaveLinkedUtility =
    linkedCombinedUtilityId || linkedElectricUtilityId || linkedGasUtilityId;

  const [{ isPaid }] = useSubscriptionType("saas");
  const {
    detailsContent: editOfficeDetails,
    powerContent: editOfficePower,
    heatContent: editOfficeHeat,
    footer: editOfficeDetailsFooter
  } = useEditOfficeDetails(office, setSelectedOffice);
  const officeStatuses = useOfficeStatuses(endDate);
  const utilityApiFeatures = useFeatureIsOn("utilityapi-features");

  const chartEmissions = emissions.map((emission) => {
    const { source } = emission;

    const warningObj =
      source === "default-utilities"
        ? { warning: officeStatuses.missingEmissions }
        : {};

    return { ...emission, ...warningObj };
  });

  const generateOfficeDetailsContent = () => {
    const statusObj = officeStatuses[status];

    if (status !== "active" && !emissions.length) {
      return <EmissionsDetailsWarning {...statusObj} />;
    }

    const { tooltip, color, icon } = statusObj;

    const warning =
      status === "active" ? {} : { warning: { text: tooltip, color, icon } };

    const isPayingUtilities =
      electricityInLease === false ||
      (hasHeat !== false && heatInLease === false);
    const showUtilityPopup =
      availableUtilitiesProviders?.length &&
      isPayingUtilities &&
      !doesOfficeHaveLinkedUtility &&
      isPaid &&
      utilityApiFeatures;

    return (
      <>
        {utilitiesFormOpen && (
          <LinkUtilitiesForm
            setOpen={setUtilitiesFormOpen}
            utilitiesProvidersList={availableUtilitiesProviders}
          />
        )}
        <Grid container spacing={2} direction="column">
          <Grid item>
            <EmissionsDetails
              {...warning}
              emissions={emissions}
              chartEmissions={chartEmissions}
              setSelectedTransaction={setSelectedTransaction}
              type="offices"
              name={name}
              id={id}
            />
          </Grid>
          {!!showUtilityPopup && (
            <Grid item>
              <WarningPopup
                type="success"
                warningTitle={`This office falls under the utility provider${
                  availableUtilitiesProviders?.length > 1 ? "s" : ""
                } ${availableUtilitiesProvidersString}. When you connect ${
                  availableUtilitiesProviders?.length > 1
                    ? "one of these providers"
                    : "this provider"
                }, Aclymate can completely automate your utility emissions, no more manual entry!`}
                warningInput={
                  <Grid
                    container
                    justifyContent="center"
                    direction="row"
                    spacing={2}
                  >
                    {!isPaid && (
                      <Grid item>
                        <AnalyticsFeatureIcon />
                      </Grid>
                    )}
                    <Grid item>
                      <Button
                        color="primary"
                        variant="contained"
                        onClick={() => setUtilitiesFormOpen(true)}
                        disabled={!isPaid}
                      >
                        Connect utility provider
                      </Button>
                    </Grid>
                  </Grid>
                }
              />
            </Grid>
          )}
        </Grid>
      </>
    );
  };

  const detailsView =
    status !== "closed"
      ? [
          {
            label: "Details",
            value: "details",
            content: editOfficeDetails,
            footer: editOfficeDetailsFooter
          },
          {
            label: "Power",
            value: "power",
            content: editOfficePower,
            footer: editOfficeDetailsFooter
          },
          {
            label: "Heat",
            value: "heat",
            content: editOfficeHeat,
            footer: editOfficeDetailsFooter
          }
        ]
      : [];

  const views = [
    {
      label: "Emissions",
      value: "emissions",
      content: generateOfficeDetailsContent()
    },
    ...detailsView
  ];

  const buildFooterInputSubcategories = () => {
    const powerNotInLease = electricityInLease === false;
    const heatNotInLease = hasHeat !== false && heatInLease === false;

    if (powerNotInLease && heatNotInLease && combinedUtility) {
      return [{ subcategory: "utilities" }];
    }

    const subcategories = [];
    if (heatNotInLease) {
      subcategories.push({ subcategory: "gas" });
    }
    if (powerNotInLease) {
      subcategories.push({ subcategory: "electricity" });
    }

    return subcategories;
  };

  const { palette } = mainTheme;
  const powerNotInLease = electricityInLease === false;
  const heatNotInLease = hasHeat !== false && heatInLease === false;

  const buildOfficeOfficeCsvUploadButton = (subcategory, tooltip, color) => ({
    icon: faFileArrowUp,
    onClick: () => {
      setSelectedOfficeCsvUpload({ id, name, subcategory });
      setSelectedOffice(null);
    },
    tooltip,
    color
  });

  const otherFooterInputsArray = [
    ...(powerNotInLease || heatNotInLease
      ? [
          {
            icon: faFileArrowUp,
            onClick: () => {
              setSelectedCsvUploadOffice({ id, name });
              setSelectedOffice(null);
            },
            tooltip: "Upload utilities CSV"
          }
        ]
      : []),
    buildOfficeOfficeCsvUploadButton(
      "purchased-water",
      "Upload water CSV",
      palette["purchased-water"].main
    ),
    buildOfficeOfficeCsvUploadButton(
      "steam",
      "Upload steam CSV",
      palette.steam.main
    ),
    buildOfficeOfficeCsvUploadButton(
      "chill-water",
      "Upload chill water CSV",
      palette["chill-water"].main
    ),
    buildOfficeOfficeCsvUploadButton(
      "refrigerants",
      "Upload refrigerants CSV",
      palette.refrigerants.main
    )
  ];

  return (
    <SettingsObjectDetailsSlider
      type="offices"
      footerInputSubcategories={[
        { subcategory: "spend-based" },
        ...buildFooterInputSubcategories()
      ]}
      settingsObject={office}
      setSelectedObject={setSelectedOffice}
      views={views}
      noFooter={status === "closed"}
      otherFooterInputs={otherFooterInputsArray}
    />
  );
};
export default OfficeDetailsSlider;
